import React from "react";
import { Separator } from "components/ui/separator";
import { Github, Linkedin, Mail } from "lucide-react";
import { navLinks } from "data/navigation";
import { scrollToSection } from "lib/scroll";
import { useLanguage } from "contexts/LanguageContext";

const socialLinks = [
  {
    icon: Github,
    href: "https://github.com/ZakariaDani",
    label: "GitHub",
  },
  {
    icon: Linkedin,
    href: "https://www.linkedin.com/in/zakaria-dani-5a2961121/",
    label: "LinkedIn",
  },
  {
    icon: Mail,
    href: "mailto:zakariadani25@gmail.com",
    label: "Email",
  },
];

const Footer: React.FC = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute("href");
    if (href) {
      scrollToSection(href);
    }
  };

  return (
    <footer className="border-t bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold">
              ZD<span className="text-primary">.</span>
            </h3>
            <p className="text-sm text-muted-foreground">
              {t.footer.tagline}
            </p>
            <div className="flex gap-4">
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target={link.href.startsWith("http") ? "_blank" : undefined}
                  rel={
                    link.href.startsWith("http")
                      ? "noopener noreferrer"
                      : undefined
                  }
                  className="w-10 h-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-colors"
                  aria-label={link.label}
                >
                  <link.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">{t.footer.quickLinks}</h4>
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={handleNavClick}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {t.nav[link.key]}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">{t.footer.getInTouch}</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>{t.footer.openToOpportunities}</p>
              <p>
                <a
                  href="mailto:zakariadani25@gmail.com"
                  className="hover:text-primary transition-colors"
                >
                  zakariadani25@gmail.com
                </a>
              </p>
              <p>{t.footer.remote}</p>
            </div>
          </div>
        </div>

        <Separator className="mb-8" />

        {/* Bottom Section */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <p>© {currentYear} Zakaria Dani. {t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
